<template>
  <div :class="{ 'align-items-center d-flex w-100': esVistaSimplificada && !esVistaTimeline }">
    <div v-if="esVistaSimplificada && chartData" class="mb-0 mr-2 d-flex align-items-center">
      <div class="mr-2">
        <div v-if="responsable" class="badge badge-dark badge-pill text-monospace text-uppercase" :class="{ 'align-text-bottom': !esVistaTimeline }" :title="`Responsable: ${responsable}`" v-tooltip>{{ siglas }}</div>
        <i v-else class="fa-regular fa-user tooltiper align-text-bottom" title="Cap responsable asignat" v-tooltip></i>
      </div>
      <div class="mr-2">
        <span class="badge badge-secondary" title="Marge" :style="{ 'min-width': '65px' }" v-html="chartData.labels[0].textoPrincipal ? `${Math.round(chartData.labels[0].textoPrincipal)}%` : 'N/D'" v-tooltip></span>
      </div>
      <button v-if="esVistaTimeline" class="btn btn-outline-secondary btn-sm collapsed" data-toggle="collapse" :data-target="`.collapsible[data-collapse='${id}']`" aria-expanded="false"></button>
      <ProgressProyecto v-if="esVistaSimplificada & !esVistaTimeline" :title="desgloseTiempo" :label="'Hores disponibles'" :percentage="porcentajeTiempo" :backgroundColor="chartData.datasets[0].backgroundColor[0]" />
      <ProgressProyecto v-if="esVistaSimplificada & !esVistaTimeline" :title="desgloseCoste" :label="'Pressupost disponible'" :percentage="porcentajeCoste" :backgroundColor="chartData.datasets[1].backgroundColor[0]" />
    </div>
    <Doughnut v-else-if="chartData" :chart-options="chartProyectosOptions" :chart-data="chartData" :styles="chartProyectoStyles" :plugins="chartProyectosPlugins" />
    <div v-if="esVistaTimeline" :class="{ 'collapsible collapse': esVistaTimeline }" :data-collapse="id">
      <div class="d-flex mt-3">
        <ProgressProyecto :title="desgloseTiempo" :label="'Hores disponibles'" :percentage="porcentajeTiempo" :backgroundColor="chartData.datasets[0].backgroundColor[0]" />
        <ProgressProyecto :title="desgloseCoste" :label="'Pressupost disponible'" :percentage="porcentajeCoste" :backgroundColor="chartData.datasets[1].backgroundColor[0]" />
      </div>
    </div>
    <slot></slot>
  </div>
</template>
<style scoped>
[data-toggle="collapse"]::before {
  font-family: "Font Awesome 6 Free";
  font-weight: 900;
  content: "\f077";
}

[data-toggle="collapse"].collapsed::before {
  font-family: "Font Awesome 6 Free";
  font-weight: 900;
  content: "\f078";
}
</style>

<script>
import ProgressProyecto from "./ProgressProyecto.vue";
import { Doughnut } from "vue-chartjs/legacy";

import { Chart as ChartJS, Title, Tooltip, Legend, ArcElement, CategoryScale, Chart } from "chart.js";
import { calcularPorcentajeTiempo, calcularPorcentajeCoste, mapearPorcentajeANivel } from "@/utils/projectStats";

ChartJS.register(Title, Tooltip, Legend, ArcElement, CategoryScale);
export default {
  name: "EstadisticaProyecto",
  inject: ["colors"],
  props: {
    prevision: Object,
    esVistaSimplificada: Boolean,
    responsable: String,
    esVistaTimeline: Boolean,
    id: Number,
  },
  components: {
    Doughnut,
    ProgressProyecto,
  },
  data() {
    return {
      chartProyectosOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            callbacks: {
              label: function (context) {
                return context.label[context.dataset.label];
              },
            },
          },
          textoPrincipal: {
            sufijo: "%",
            fontSizeRelativa: 350,
          },
        },
      },
      chartProyectoStyles: {
        position: "relative",
      },
      chartProyectosPlugins: [
        {
          id: "textoPrincipal",
          beforeDraw: (chart, args, options) => {
            const valor = chart.data.labels[0].textoPrincipal;
            const text = valor ? `${Math.round(valor)}${options.sufijo ?? ""}` : "N/D";

            this.ponerTextoPrincipal(chart, args, options, text);
          },
        },
      ],
    };
  },
  computed: {
    siglas() {
      return this.responsable ? this.responsable.split(" ").map(n => n[0]).join("") : null;
    },
    porcentajeTiempo() {
      return calcularPorcentajeTiempo(this.previsionCompleta?.perfiles, true);
    },
    porcentajeCoste() {
      return calcularPorcentajeCoste(this.previsionCompleta?.totales_costes, true);
    },
    desgloseTiempo() {
      if (!this.chartData) return;

      const labels = [...this.chartData.labels[Array.isArray(this.chartData.labels[1].tiempo) ? 1 : 0].tiempo];

      labels.splice(1, 0, this.chartData.labels[Array.isArray(this.chartData.labels[1].tiempo) ? 0 : 1].tiempo);

      return labels.join("\n");
    },
    desgloseCoste() {
      if (!this.chartData) return;

      return this.chartData.labels[Array.isArray(this.chartData.labels[1].coste) ? 1 : 0].coste.join("\n");
    },
    previsionCompleta() {
      if (!Object.values(this.prevision).length) return;

      const prevision = { ...this.prevision };

      prevision.total_presupuesto = Math.round(prevision.totales_costes.prevision) / (1 - (Math.round(prevision.margen_prevision) / 100));

      const margeReal = prevision.total_presupuesto - (prevision.totales_costes.realidad ?? 0);
      prevision.margenCalculado = Math.round((margeReal / prevision.total_presupuesto) * 100);

      return prevision;
    },
    chartData() {
      if (!this.previsionCompleta?.perfiles) return;

      const tiempoRealidad = Object.values(this.previsionCompleta.perfiles).reduce((a, b) => a + parseFloat(b.realidad || 0), 0);
      const tiempoPrevision = Object.values(this.previsionCompleta.perfiles).reduce((a, b) => a + parseFloat(b.prevision || 0), 0);
      const totalTiempoPrevision = tiempoPrevision - tiempoRealidad;
      const costeRealidad = this.previsionCompleta.totales_costes.realidad;
      const costePrevision = this.previsionCompleta.totales_costes.prevision;
      const totalCostePrevision = costePrevision - costeRealidad;
      const dataLabelTiempo = [`Hores realitzades: ${Math.round(tiempoRealidad)}h`, [`Hores previstes: ${Math.round(tiempoPrevision)}h`, `Hores disponibles: ${Math.round(totalTiempoPrevision)}h`]];
      const dataLabelCoste = [`Cost real: ${Math.round(costeRealidad)}€`, [`Pressupost: ${Math.round(costePrevision)}€`, `Resten: ${Math.round(totalCostePrevision)}€`]];

      if (totalTiempoPrevision < 0) {
        dataLabelTiempo.reverse();
      }

      if (totalCostePrevision < 0) {
        dataLabelCoste.reverse();
      }

      const dataTiempo = totalTiempoPrevision >= 0 ? [tiempoRealidad, totalTiempoPrevision] : [totalTiempoPrevision, tiempoRealidad];
      const dataCoste = totalCostePrevision >= 0 ? [costeRealidad, totalCostePrevision] : [totalCostePrevision, costeRealidad];

      return {
        labels: [
          { tiempo: dataLabelTiempo[0], coste: dataLabelCoste[0], textoPrincipal: tiempoPrevision || costePrevision ? this.previsionCompleta.margenCalculado : null },
          { tiempo: dataLabelTiempo[1], coste: dataLabelCoste[1] },
        ],
        datasets: [
          {
            backgroundColor: totalTiempoPrevision >= 0 ? [this.colorearPeligrosidad(calcularPorcentajeTiempo(this.previsionCompleta.perfiles, true)), this.colors.softGray] : [this.colors.danger, this.colors.danger],
            data: dataTiempo,
            label: "tiempo",
          },
          {
            backgroundColor: totalCostePrevision >= 0 ? [this.colorearPeligrosidad(calcularPorcentajeCoste(this.previsionCompleta.totales_costes, true)), this.colors.softGray] : [this.colors.danger, this.colors.danger],
            data: dataCoste,
            label: "coste",
          },
        ],
      };
    },
  },
  methods: {
    ponerTextoPrincipal: function (chart, args, options, text) {
      const width = chart.chartArea.width;
      const height = chart.chartArea.height;
      const ctx = chart.ctx;

      ctx.restore();
      const fontSize = (height / (options.fontSizeRelativa ?? 200)).toFixed(2);
      ctx.font = fontSize + "em sans-serif";
      ctx.textBaseline = "middle";

      const textX = Math.round((width - ctx.measureText(text).width) / 2);
      const textY = chart.chartArea.top + height / 2;

      ctx.fillText(text, textX, textY);
      ctx.save();
    },
    colorearPeligrosidad(porcentaje) {
      return this.colors[mapearPorcentajeANivel(porcentaje)];
    },
    actualizarTooltips() {
      document.querySelectorAll(".tooltiper").forEach((element) => {
        $(element).tooltip("dispose");

        this.$nextTick(() => $(element).tooltip());
      });
    },
  },
  watch: {
    desgloseTiempo() {
      this.actualizarTooltips();
    },
    desgloseCoste() {
      this.actualizarTooltips();
    },
    responsable() {
      this.actualizarTooltips();
    },
  },
};
</script>